import { Button, Divider, Menu, MenuItem, PopoverOrigin, Stack, styled, Typography } from '@mui/material';
import { useCallback, useState } from 'react';

import { formatPeriod } from 'modules/common/components/VerifiedStatementUpload/utils/formatMonthList';
import { periodKey } from 'modules/common/components/VerifiedStatementUpload/utils/periods';
import CheckSmallIcon from 'modules/common/components/Icons/CheckSmallIcon';
import { StatementPeriod } from 'services/bookkeeping/queries/useStatementCoverage';

/** Anchored under the trigger and right-aligned with it. Constant, so hoisted out of render. */
const MENU_ANCHOR_ORIGIN: PopoverOrigin = { vertical: 'bottom', horizontal: 'right' };
const MENU_TRANSFORM_ORIGIN: PopoverOrigin = { vertical: 'top', horizontal: 'right' };

/**
 * The selected-month tick.
 *
 * Hidden rather than unmounted: every row keeps the slot whether or not its tick is showing,
 * so the month labels don't shift horizontally as the selection moves down the list. Driven by
 * a prop rather than a call-site `sx` so the styling stays out of the JSX, following
 * `UploadedFileRow`'s `RowContainer`.
 */
const SelectedTick = styled(CheckSmallIcon, {
    shouldForwardProp: (prop) => prop !== 'isSelected',
})<{ isSelected: boolean }>(({ isSelected }) => ({
    fontSize: 16,
    visibility: isSelected ? 'visible' : 'hidden',
}));

interface PeriodMenuItemProps {
    readonly period: StatementPeriod;
    readonly isSelected: boolean;
    readonly onSelect: (period: StatementPeriod) => void;
}

/** One month. Its own component so the click handler closes over the period without an arrow function in the parent's JSX. */
function PeriodMenuItem({ period, isSelected, onSelect }: Readonly<PeriodMenuItemProps>) {
    const handleClick = useCallback(() => onSelect(period), [onSelect, period]);

    return (
        <MenuItem onClick={handleClick} sx={{ py: 1, px: 2 }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                <Typography variant="body2" sx={{ flex: 1 }}>
                    {formatPeriod(period)}
                </Typography>
                <SelectedTick isSelected={isSelected} />
            </Stack>
        </MenuItem>
    );
}

interface StatementPeriodMenuProps {
    /** Passed back out through the callbacks so the parent can hand down stable handlers. */
    readonly documentUuid: string;
    /**
     * The months this row may be moved to, taken or not.
     *
     * Usually every month the account was asked for. A row of a file that carries several
     * statements is offered fewer: a month one of its sibling rows already holds is withheld,
     * because moving onto it has no answer that does not drop a month the member never mentioned.
     */
    readonly periods: StatementPeriod[];
    /** The month this file currently holds, if it holds one. */
    readonly selectedPeriod?: StatementPeriod;
    /**
     * Whether this row has an assignment to undo.
     *
     * Not "the member chose this": coverage reports where a row sits, never whether we inferred
     * the month or an earlier correction moved it, so every credited row reads as assigned. It
     * gates the undo affordance, and undoing an uncorrected row is a no-op on the server.
     */
    readonly isAssigned: boolean;
    readonly onSelect: (documentUuid: string, period: StatementPeriod) => void;
    readonly onClear: (documentUuid: string) => void;
    /** Named in the control's accessible label, so rows stay distinguishable. */
    readonly fileName: string;
    /**
     * Appended to the accessible label. A multi-month document occupies one row per month it
     * covers, and the file name alone would name every one of those controls identically.
     */
    readonly labelSuffix?: string;
}

/**
 * The per-row overflow menu for correcting which month a statement counts for.
 *
 * Lists every required month including ones already taken, by design: reassigning a taken
 * month is the only way to fix the common mix-up of two files reading as the same month. The
 * displaced file drops to `unmatched` rather than being lost. Settled rows only.
 */
export default function StatementPeriodMenu({
    documentUuid,
    periods,
    selectedPeriod,
    isAssigned,
    onSelect,
    onClear,
    fileName,
    labelSuffix = '',
}: Readonly<StatementPeriodMenuProps>) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleOpen = useCallback((event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget), []);
    const handleClose = useCallback(() => setAnchorEl(null), []);

    const handleSelect = useCallback(
        (period: StatementPeriod) => {
            setAnchorEl(null);
            onSelect(documentUuid, period);
        },
        [onSelect, documentUuid]
    );

    const handleClear = useCallback(() => {
        setAnchorEl(null);
        onClear(documentUuid);
    }, [onClear, documentUuid]);

    if (periods.length === 0) {
        // No books start date resolved yet, so there is nothing to match against. An empty
        // menu would be a control that opens onto nothing.
        return null;
    }

    const selectedKey = selectedPeriod ? periodKey(selectedPeriod) : null;
    // The accessible name starts with the visible words, so a screen reader announces the same action.
    const actionLabel = selectedPeriod ? 'Change month' : 'Choose month';

    return (
        <>
            {/*
             * Words, not a ⋮: this is how a member files a statement we could not read, and an
             * unlabelled icon beside a "please try again" message read as a dead end. The card's
             * own ⋮ (the waiver) is a different control, so two identical icons also hid this one.
             */}
            <Button
                variant="text"
                color="secondary"
                size="small"
                onClick={handleOpen}
                aria-label={`${actionLabel} for ${fileName}${labelSuffix}`}>
                {actionLabel}
            </Button>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                disableAutoFocusItem
                anchorOrigin={MENU_ANCHOR_ORIGIN}
                transformOrigin={MENU_TRANSFORM_ORIGIN}>
                {periods.map((period) => {
                    const key = periodKey(period);
                    return (
                        <PeriodMenuItem
                            key={key}
                            period={period}
                            isSelected={key === selectedKey}
                            onSelect={handleSelect}
                        />
                    );
                })}

                {/*
                 * Only once there is a member choice to undo — on an untouched row it would
                 * suggest our own reading can be cleared, which it can't. Undoing needs no
                 * confirmation, matching how un-checking the waiver is immediate.
                 */}
                {isAssigned && <Divider />}
                {isAssigned && (
                    <MenuItem onClick={handleClear} sx={{ py: 1, px: 2 }}>
                        <Typography variant="body2">Undo my change</Typography>
                    </MenuItem>
                )}
            </Menu>
        </>
    );
}
